import React, { useEffect, useMemo, useState } from 'react'
import { consultarNotificacoes } from '../services/monitoramentoNotificacoes'

const vazioOpcao = { email: true, whatsapp: false }

export default function MonitoramentoAvisos({ aberto, onFechar, admin, empresaInicial, empresas }) {
  const [empresaId, setEmpresaId] = useState(empresaInicial || '')
  const [usuarios, setUsuarios] = useState([])
  const [liberados, setLiberados] = useState([])
  const [busca, setBusca] = useState('')
  const [selecionado, setSelecionado] = useState(null)
  const [opcao, setOpcao] = useState(vazioOpcao)
  const [mensagem, setMensagem] = useState('')
  const [erro, setErro] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [aba, setAba] = useState('destinatarios')
  const [historico, setHistorico] = useState([])
  const [carregandoHistorico, setCarregandoHistorico] = useState(false)
  const [versaoHistorico, setVersaoHistorico] = useState(0)

  useEffect(() => {
    if (!aberto) return
    setEmpresaId(empresaInicial || empresas?.[0]?.id || '')
    setBusca('')
    setSelecionado(null)
    setOpcao(vazioOpcao)
    setMensagem('')
    setErro('')
    setAba('destinatarios')
  }, [aberto, empresaInicial, empresas])

  useEffect(() => {
    if (!aberto || !empresaId) return
    let vivo = true
    setErro('')
    setSelecionado(null)
    setBusca('')
    consultarNotificacoes('destinatarios', { empresaId })
      .then((dados) => {
        if (!vivo) return
        const lista = dados.usuarios || []
        setUsuarios(lista)
        setLiberados((dados.destinatarios || []).map((item) => {
          const usuario = lista.find((u) => u.id === item.usuario_id) || {}
          return {
            usuarioId: item.usuario_id,
            nome: usuario.nome || 'Usuário',
            email: usuario.email || '',
            telefone: usuario.telefone || '',
            emailAviso: !!item.email,
            whatsapp: !!item.whatsapp
          }
        }))
      })
      .catch((e) => vivo && setErro(e.message))
    return () => {
      vivo = false
    }
  }, [aberto, empresaId])

  useEffect(() => {
    if (!aberto || !empresaId || aba !== 'historico') return
    let vivo = true
    setCarregandoHistorico(true)
    consultarNotificacoes('historico', { empresaId })
      .then((dados) => {
        if (vivo) setHistorico(dados.historico || [])
      })
      .catch((e) => vivo && setErro(e.message))
      .finally(() => vivo && setCarregandoHistorico(false))
    return () => {
      vivo = false
    }
  }, [aberto, empresaId, aba, versaoHistorico])

  const sugestoes = useMemo(() => {
    const texto = busca.trim().toLowerCase()
    if (texto.length < 2) return []
    return usuarios
      .filter((usuario) => !liberados.some((item) => item.usuarioId === usuario.id))
      .filter((usuario) => String(usuario.nome || '').toLowerCase().includes(texto))
      .slice(0, 8)
  }, [busca, usuarios, liberados])

  if (!aberto) return null

  const escolher = (usuario) => {
    setSelecionado(usuario)
    setBusca(usuario.nome || '')
    setOpcao(vazioOpcao)
    setErro('')
  }

  const incluir = () => {
    if (!selecionado) {
      setErro('Busque e selecione um usuário.')
      return
    }
    if (!opcao.email && !opcao.whatsapp) {
      setErro('Marque e-mail, WhatsApp ou os dois.')
      return
    }
    if (opcao.whatsapp && !String(selecionado.telefone || '').trim()) {
      setErro('Este usuário não tem telefone no cadastro. Inclua o número em Usuários antes de marcar WhatsApp.')
      return
    }
    setLiberados((lista) => [
      ...lista,
      {
        usuarioId: selecionado.id,
        nome: selecionado.nome,
        email: selecionado.email || '',
        telefone: selecionado.telefone || '',
        emailAviso: opcao.email,
        whatsapp: opcao.whatsapp
      }
    ])
    setSelecionado(null)
    setBusca('')
    setOpcao(vazioOpcao)
    setErro('')
    setMensagem('')
  }

  const salvar = async () => {
    setSalvando(true)
    setErro('')
    setMensagem('')
    try {
      await consultarNotificacoes('salvarDestinatarios', {
        empresaId,
        destinatarios: liberados.map((item) => ({
          usuarioId: item.usuarioId,
          email: item.emailAviso,
          whatsapp: item.whatsapp
        }))
      })
      setMensagem('Destinatários salvos. O aviso sai na próxima verificação.')
    } catch (e) {
      setErro(e.message)
    } finally {
      setSalvando(false)
    }
  }

  const verificar = async () => {
    setSalvando(true)
    setErro('')
    setMensagem('')
    try {
      const dados = await consultarNotificacoes('varrer')
      const extra = (dados.avisosMarca || []).filter(Boolean).join(' ')
      setMensagem(
        dados.pulado
          ? dados.mensagem
          : `${dados.novos || 0} aviso(s) novo(s). E-mail: ${dados.emails || 0}. WhatsApp: ${dados.whatsapps || 0}. ${extra}`
      )
      setVersaoHistorico((atual) => atual + 1)
    } catch (e) {
      setErro(e.message)
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div className="modal fade show d-block" style={{ background: 'rgba(15,23,42,.45)' }} role="dialog">
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Quem recebe os avisos</h5>
            <button type="button" className="btn btn-light" aria-label="Fechar" onClick={onFechar}>×</button>
          </div>
          <div className="modal-body">
            <p className="text-muted small">
              O e-mail e o telefone vêm do cadastro do usuário. O WhatsApp só pode ser marcado quando o telefone já está preenchido.
            </p>
            {admin && (
              <div className="mb-3">
                <label className="form-label" htmlFor="avisos-empresa">Empresa</label>
                <select id="avisos-empresa" className="form-select" value={empresaId} onChange={(e) => setEmpresaId(e.target.value)}>
                  {(empresas || []).map((empresa) => (
                    <option key={empresa.id} value={empresa.id}>{empresa.nome}</option>
                  ))}
                </select>
              </div>
            )}

            <ul className="nav nav-tabs mb-3">
              <li className="nav-item">
                <button type="button" className={`nav-link ${aba === 'destinatarios' ? 'active' : ''}`} onClick={() => setAba('destinatarios')}>
                  Destinatários
                </button>
              </li>
              <li className="nav-item">
                <button type="button" className={`nav-link ${aba === 'historico' ? 'active' : ''}`} onClick={() => setAba('historico')}>
                  Histórico
                </button>
              </li>
            </ul>

            {aba === 'historico' ? (
              <HistoricoEnvios itens={historico} carregando={carregandoHistorico} />
            ) : (
            <>
            <label className="form-label" htmlFor="avisos-busca">Buscar usuário</label>
            <input
              id="avisos-busca"
              className="form-control"
              placeholder="Digite o nome"
              value={busca}
              autoComplete="off"
              onChange={(e) => {
                setBusca(e.target.value)
                setSelecionado(null)
              }}
            />
            {sugestoes.length > 0 && !selecionado && (
              <div className="list-group mt-1">
                {sugestoes.map((usuario) => (
                  <button
                    key={usuario.id}
                    type="button"
                    className="list-group-item list-group-item-action"
                    onClick={() => escolher(usuario)}
                  >
                    <strong>{usuario.nome}</strong>
                    <span className="text-muted small ms-2">{usuario.email || 'Sem e-mail'}</span>
                  </button>
                ))}
              </div>
            )}

            {selecionado && (
              <div className="border rounded-3 p-3 mt-3">
                <div className="fw-semibold">{selecionado.nome}</div>
                <div className="text-muted small mb-2">
                  {selecionado.email || 'Sem e-mail'}
                  {' · '}
                  {selecionado.telefone || 'Sem telefone no cadastro'}
                </div>
                <div className="d-flex flex-wrap gap-3 align-items-center">
                  <label className="form-check mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={opcao.email}
                      onChange={(e) => setOpcao((atual) => ({ ...atual, email: e.target.checked }))}
                    />
                    <span className="form-check-label">E-mail</span>
                  </label>
                  <label className="form-check mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={opcao.whatsapp}
                      disabled={!String(selecionado.telefone || '').trim()}
                      onChange={(e) => setOpcao((atual) => ({ ...atual, whatsapp: e.target.checked }))}
                    />
                    <span className="form-check-label">WhatsApp</span>
                  </label>
                  <button type="button" className="btn btn-sm btn-primary" onClick={incluir}>Incluir</button>
                </div>
              </div>
            )}

            <h6 className="mt-4">Usuários liberados</h6>
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>E-mail</th>
                    <th>Telefone</th>
                    <th>Aviso</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {liberados.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-muted">Nenhum usuário liberado.</td>
                    </tr>
                  )}
                  {liberados.map((item) => (
                    <tr key={item.usuarioId}>
                      <td>{item.nome}</td>
                      <td>{item.email || '—'}</td>
                      <td>{item.telefone || '—'}</td>
                      <td>
                        {[item.emailAviso && 'E-mail', item.whatsapp && 'WhatsApp'].filter(Boolean).join(' · ') || '—'}
                      </td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => setLiberados((lista) => lista.filter((atual) => atual.usuarioId !== item.usuarioId))}
                        >
                          Remover
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
            )}
            {erro && <p className="text-danger small mt-3 mb-0">{erro}</p>}
            {mensagem && <p className="text-success small mt-3 mb-0">{mensagem}</p>}
          </div>
          <div className="modal-footer">
            {admin && (
              <button type="button" className="btn btn-outline-secondary me-auto" onClick={verificar} disabled={salvando}>
                Verificar agora
              </button>
            )}
            <button type="button" className="btn btn-light" onClick={onFechar}>Fechar</button>
            {aba === 'destinatarios' && (
            <button type="button" className="btn btn-primary" onClick={salvar} disabled={salvando || !empresaId}>
              {salvando ? 'Salvando…' : 'Salvar'}
            </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const NIVEL = { inativo: 'Offline de dia', falha: 'Falha', atencao: 'Alerta' }

function horaEnvio(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function HistoricoEnvios({ itens, carregando }) {
  if (carregando) return <p className="text-muted small mb-0">Carregando histórico…</p>
  return (
    <div className="table-responsive">
      <table className="table table-sm align-middle mb-0">
        <thead>
          <tr>
            <th>Quando</th>
            <th>Usuário</th>
            <th>Canal</th>
            <th>Usina</th>
            <th>Resultado</th>
          </tr>
        </thead>
        <tbody>
          {itens.length === 0 && (
            <tr>
              <td colSpan="5" className="text-muted">Nenhum aviso enviado ainda.</td>
            </tr>
          )}
          {itens.map((item) => (
            <tr key={item.id}>
              <td>{horaEnvio(item.enviadoEm)}</td>
              <td>{item.usuario}</td>
              <td>{item.canal === 'whatsapp' ? 'WhatsApp' : 'E-mail'}</td>
              <td>
                {item.usina}
                <div className="text-muted small">{[NIVEL[item.nivel] || item.nivel, item.marca].filter(Boolean).join(' · ')}</div>
              </td>
              <td className={item.erro ? 'text-danger' : 'text-success'}>{item.erro || 'Enviado'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
